import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { EventTypes } from '../../../constants/event';
import { selectMenuVersion } from '../../../reducers/menuSlice';
import {
  IMessageReceived,
  messagingActions,
} from '../../../reducers/messagingSlice';
import { selectStage } from '../../../reducers/restaurantSlice';
import logger from '../../../utils/logger';
import { SLICE_NAME } from './aiSuggestions.constants';
import {
  IAISuggestionsState,
  IAISuggestionTransmissionMessage,
  SuggestionVerdict,
} from './aiSuggestions.props';

const initialState: IAISuggestionsState = {
  byType: {},
};

export const aiSuggestionsSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    dismissSuggestion: (
      state,
      {
        payload,
      }: PayloadAction<{ suggestionType: string; suggestionId?: string }>
    ) => {
      delete state.byType[payload.suggestionType];
    },
    // Record the request_id of the agent_cart we just emitted so we can
    // correlate (and discard stale) AI suggestions.
    setLastAgentCartRequestId: (state, { payload }: PayloadAction<string>) => {
      state.lastRequestId = payload;
    },
    clearSuggestions: () => initialState,
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere.
  extraReducers: (builder) => {
    builder.addCase(
      messagingActions.messageReceived,
      (state, action: PayloadAction<IMessageReceived>) => {
        const {
          messages,
          taskRouter,
          sessionBoundarySessionId,
          isSessionBufferedMessages,
        } = action.payload;
        // HITL-1534: a new car at the board ends the previous order's context,
        // so anything still held belongs to a customer who has driven off.
        //
        // This has to key off the websocket `car_enter` EVENT, not the
        // `messagingActions.carEnter` ACTION in the matcher below: that action is
        // a local reducer dispatched ONLY by the task-router hook on
        // task-assigned, so on the non-task-router path nothing ever cleared this
        // and the previous car's closing suggestion survived into the next
        // session. Mirrors the same per-car reset in ai.slice / unresolvedItems.
        //
        // `lastRequestId` is cleared alongside `byType` (i.e. the full
        // initialState, not just the map): left set, it belongs to the departed
        // car's agent_cart, and the staleness check below would then silently drop
        // the new session's first request-correlated suggestion.
        //
        // Skipped for replayed session-buffer messages — a buffered car_enter is
        // the CURRENT session's own history, so zeroing on it would drop a live
        // suggestion. Same guard messagingSlice applies to the transcript.
        //
        // The task-router check mirrors the one messagingSlice runs before its
        // own car_enter case, so this resets on exactly the frames that flip
        // `isCarPresent` — never on another task's car. Deliberately NOT also
        // filtering on `sessionBoundarySessionId` the way the per-suggestion
        // check below does: at a genuine back-to-back car_enter that field still
        // holds the PREVIOUS session (it clears only when the order truly ends),
        // so filtering on it would reject the very frame this reset exists for.
        if (
          !isSessionBufferedMessages &&
          messages.some(
            (message) =>
              message.event === EventTypes.carEnter &&
              !(
                taskRouter?.currentTaskId &&
                taskRouter?.taskAssignedSessionId !== message.session_id
              )
          )
        ) {
          state.byType = {};
          state.lastRequestId = undefined;
        }
        messages.forEach((message) => {
          if (message.event !== EventTypes.aiSuggestion) {
            return;
          }
          if (
            (taskRouter?.currentTaskId &&
              taskRouter?.taskAssignedSessionId !== message.session_id) ||
            (sessionBoundarySessionId &&
              sessionBoundarySessionId !== message.session_id)
          ) {
            // Message belongs to a different session — ignore.
            return;
          }
          const data = (message as IAISuggestionTransmissionMessage).data;
          if (!data?.suggestion_type) {
            return;
          }
          // Retraction: a skip verdict clears any active suggestion of this type.
          if (data.verdict === SuggestionVerdict.skip) {
            delete state.byType[data.suggestion_type];
            return;
          }
          // Stale: this suggestion answers a superseded agent_cart (the agent has
          // since sent a newer one). Proactive suggestions carry no request_id
          // and always pass.
          if (
            data.request_id &&
            state.lastRequestId &&
            data.request_id !== state.lastRequestId
          ) {
            return;
          }
          // A newer suggestion reflects the latest cart, so any previously shown
          // suggestion (of any type) is now stale — replace the map, never
          // accumulate. Only the latest suggestion is ever displayed.
          state.byType = { [data.suggestion_type]: data };
          // HITL-1429: verdict and metadata.upsell are what the accept path
          // classifies on, so they are logged as RECEIVED — that distinguishes
          // "the AI never sent it" from "we ignored it" without a breakpoint.
          logger.debug({
            message: `AI suggestion received: ${data.suggestion_type} (${
              data.suggestion_id
            }) verdict=${data.verdict} constraint=${
              data.metadata?.upsell?.constraint_name ?? 'none'
            }`,
          });
        });
      }
    );
    // A suggestion always reflects the current cart, so it must never outlive
    // the order it was made for. Reset on every order/session boundary:
    //  - carEnter: the task-router hook's local action, dispatched on
    //    task-assigned. Belt-and-braces only — it does NOT fire for a websocket
    //    car_enter event, which is why the real reset lives in the
    //    messageReceived case above (HITL-1534);
    //  - sendEndSession: the current order ended — Cancel and Finish both send
    //    an end-session message (as does the staff-handoff path);
    //  - selectStage / selectMenuVersion: stage/menu re-init (mirrors ai.slice).
    // Note none of these fire when the AI completes the order itself in auto
    // mode (the agent never clicks Finish/Cancel), so the car_enter event reset
    // above is what actually clears the map in the back-to-back case.
    builder.addMatcher(
      isAnyOf(
        selectStage.fulfilled,
        selectMenuVersion.fulfilled,
        messagingActions.carEnter,
        messagingActions.sendEndSession
      ),
      () => initialState
    );
  },
});

export const aiSuggestionsActions = aiSuggestionsSlice.actions;
export const {
  dismissSuggestion,
  setLastAgentCartRequestId,
  clearSuggestions,
} = aiSuggestionsSlice.actions;
export default aiSuggestionsSlice.reducer;
